import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { t } from '../lib/i18n';
import { paymentsAPI } from '../lib/api';
import DashboardLayout from '../components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Plus, Search, Filter, CreditCard, DollarSign, Calendar,
  CheckCircle, XCircle, Clock, Receipt, Smartphone, Phone
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const getAuthHeaders = () => {
  const token = localStorage.getItem('auth_token');
  return { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };
};

const PaymentsPage = () => {
  const { language, user } = useAuth();
  const [searchParams] = useSearchParams();
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [creating, setCreating] = useState(false);
  const [checkingPayment, setCheckingPayment] = useState(false);
  const [activeGateways, setActiveGateways] = useState([]);
  const [selectedMethod, setSelectedMethod] = useState('stripe');
  const [filters, setFilters] = useState({ status: '', payment_type: '' });

  const [formData, setFormData] = useState({
    payment_type: 'FEE',
    amount: '',
    currency: 'USD',
    description: '',
    payer_name: '',
    case_id: '',
    phone_number: ''
  });

  useEffect(() => {
    loadPayments();
    loadActiveGateways();
    const sessionId = searchParams.get('session_id');
    if (sessionId) pollPaymentStatus(sessionId);
  }, [filters, searchParams]);

  const loadActiveGateways = async () => {
    try {
      const res = await axios.get(`${API}/api/payment-gateways/active`, { headers: getAuthHeaders() });
      setActiveGateways(res.data.gateways || []);
      if (res.data.gateways?.length > 0) {
        setSelectedMethod(res.data.gateways[0].gateway_id);
      }
    } catch (err) {
      console.error('Failed to load gateways:', err);
    }
  };

  const loadPayments = async () => {
    try {
      setLoading(true);
      const params = { ...filters, limit: 100 };
      Object.keys(params).forEach(key => !params[key] && delete params[key]);
      const response = await paymentsAPI.list(params);
      setPayments(response.data);
    } catch (error) {
      toast.error(language === 'fr' ? 'Erreur de chargement' : 'Failed to load payments');
    } finally {
      setLoading(false);
    }
  };

  const pollPaymentStatus = async (sessionId, attempts = 0) => {
    if (attempts >= 5) {
      toast.info(language === 'fr' ? 'Vérifiez votre email pour confirmation' : 'Check email for confirmation');
      return;
    }
    setCheckingPayment(true);
    try {
      const response = await paymentsAPI.getStatus(sessionId);
      if (response.data.payment_status === 'paid') {
        toast.success(language === 'fr' ? 'Paiement réussi!' : 'Payment successful!');
        loadPayments();
        setCheckingPayment(false);
        window.history.replaceState({}, '', '/payments');
        return;
      } else if (response.data.status === 'expired') {
        toast.error(language === 'fr' ? 'Session expirée' : 'Session expired');
        setCheckingPayment(false);
        return;
      }
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), 2000);
    } catch (error) {
      setCheckingPayment(false);
    }
  };

  const handleCreatePayment = async (e) => {
    e.preventDefault();
    setCreating(true);

    const gw = activeGateways.find(g => g.gateway_id === selectedMethod);
    const isMobile = gw?.type === 'mobile_money';
    const isMaishaPay = gw?.gateway_id === 'maishapay';

    try {
      if (isMaishaPay) {
        const res = await axios.post(`${API}/api/payments/maishapay/initiate`, {
          amount: parseFloat(formData.amount),
          currency: formData.currency,
          payment_type: formData.payment_type,
          description: formData.description,
          payer_name: formData.payer_name,
          case_id: formData.case_id
        }, { headers: getAuthHeaders() });
        const data = res.data;

        if (data.test_mode) {
          toast.success(`${data.message} (Mode test)`);
          setShowCreateModal(false);
          loadPayments();
          setFormData({ payment_type: 'FEE', amount: '', currency: 'USD', description: '', payer_name: '', case_id: '', phone_number: '' });
        } else if (data.redirect_url && data.form_data) {
          // MaishaPay requires a POST form submission
          const form = document.createElement('form');
          form.method = 'POST';
          form.action = data.redirect_url;
          Object.entries(data.form_data).forEach(([key, value]) => {
            const input = document.createElement('input');
            input.type = 'hidden';
            input.name = key;
            input.value = value;
            form.appendChild(input);
          });
          document.body.appendChild(form);
          form.submit();
        }
      } else if (isMobile) {
        if (!formData.phone_number) {
          toast.error(language === 'fr' ? 'Numéro de téléphone requis' : 'Phone number required');
          setCreating(false);
          return;
        }
        const res = await axios.post(`${API}/api/payments/mobile-money`, {
          gateway_id: selectedMethod,
          phone_number: formData.phone_number,
          amount: parseFloat(formData.amount),
          currency: formData.currency,
          payment_type: formData.payment_type,
          description: formData.description,
          payer_name: formData.payer_name,
          case_id: formData.case_id
        }, { headers: getAuthHeaders() });
        const data = res.data;
        
        if (data.test_mode) {
          toast.success(`${data.message} (Mode test)`);
        } else {
          toast.success(data.message);
        }
        setShowCreateModal(false);
        loadPayments();
        setFormData({ payment_type: 'FEE', amount: '', currency: 'USD', description: '', payer_name: '', case_id: '', phone_number: '' });
      } else {
        const response = await paymentsAPI.create({
          ...formData,
          amount: parseFloat(formData.amount),
          fee_type: formData.payment_type
        });
        if (response.data.checkout_url) {
          window.location.href = response.data.checkout_url;
        }
      }
    } catch (error) {
      const msg = error.response?.data?.detail || error.message || (language === 'fr' ? 'Erreur de paiement' : 'Payment failed');
      toast.error(msg);
    } finally {
      setCreating(false);
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      PENDING: { cls: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400', icon: Clock },
      COMPLETED: { cls: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400', icon: CheckCircle },
      FAILED: { cls: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400', icon: XCircle },
      EXPIRED: { cls: 'bg-gray-100 text-gray-800 dark:bg-gray-800/50 dark:text-gray-400', icon: XCircle }
    };
    const style = styles[status] || styles.PENDING;
    const Icon = style.icon;
    return (
      <span className={`px-2 py-1 rounded text-xs font-medium inline-flex items-center gap-1 ${style.cls}`}>
        <Icon className="w-3 h-3" />
        {status}
      </span>
    );
  };

  const getMethodIcon = (payment) => {
    if (payment.gateway_id === 'maishapay') {
      return <Smartphone className="w-4 h-4 text-sky-600" />;
    }
    if (payment.payment_method === 'mobile_money' || payment.gateway_id) {
      return <Smartphone className="w-4 h-4 text-green-600" />;
    }
    return <CreditCard className="w-4 h-4 text-indigo-600" />;
  };

  const getMethodLabel = (payment) => {
    if (payment.gateway_name) return payment.gateway_name;
    if (payment.gateway_id === 'maishapay') return 'MaishaPay';
    if (payment.gateway_id === 'mpesa') return 'M-Pesa';
    if (payment.gateway_id === 'airtel_money') return 'Airtel Money';
    if (payment.gateway_id === 'orange_money') return 'Orange Money';
    return 'Carte bancaire';
  };

  const paymentTypes = ['FEE', 'FINE', 'TAX'];
  const statuses = ['PENDING', 'COMPLETED', 'FAILED', 'EXPIRED'];
  const selectedGw = activeGateways.find(g => g.gateway_id === selectedMethod);
  const isMobileMethod = selectedGw?.type === 'mobile_money';
  const isMaishaPayMethod = selectedGw?.gateway_id === 'maishapay';
  const needsPhone = isMobileMethod && !isMaishaPayMethod;
  const noGateways = activeGateways.length === 0;

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="payments-page">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="font-heading text-3xl font-bold tracking-tight">
              {t(language, 'payments.title')}
            </h1>
            <p className="text-muted-foreground mt-1">
              {language === 'fr' ? 'Gestion des paiements judiciaires' : 'Judicial payment management'}
            </p>
          </div>
          <Button
            className="gap-2"
            data-testid="new-payment-btn"
            onClick={() => setShowCreateModal(true)}
            disabled={noGateways}
          >
            <Plus className="w-4 h-4" />
            {t(language, 'payments.newPayment')}
          </Button>
        </div>

        {noGateways && (
          <Card className="border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20">
            <CardContent className="p-4 flex items-center gap-3">
              <CreditCard className="w-5 h-5 text-amber-600 shrink-0" />
              <p className="text-sm">
                {language === 'fr'
                  ? 'Aucune passerelle de paiement activée. Allez dans Administration > Configuration Paiements pour en activer une.'
                  : 'No payment gateway enabled. Go to Administration > Payment Config to enable one.'}
              </p>
            </CardContent>
          </Card>
        )}

        {checkingPayment && (
          <Card className="border-yellow-200 dark:border-yellow-800 bg-yellow-50 dark:bg-yellow-900/20">
            <CardContent className="p-4 flex items-center gap-3">
              <div className="animate-spin w-5 h-5 border-2 border-yellow-600 border-t-transparent rounded-full" />
              <span>{language === 'fr' ? 'Vérification du paiement...' : 'Checking payment status...'}</span>
            </CardContent>
          </Card>
        )}

        {/* Create Payment Modal */}
        <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="font-heading">{t(language, 'payments.newPayment')}</DialogTitle>
            </DialogHeader>

            {activeGateways.length > 1 && (
              <div className="space-y-2">
                <Label>{language === 'fr' ? 'Mode de paiement' : 'Payment method'}</Label>
                <div className="grid grid-cols-2 gap-2">
                  {activeGateways.map(gw => (
                    <button
                      key={gw.gateway_id}
                      type="button"
                      onClick={() => setSelectedMethod(gw.gateway_id)}
                      className={`p-3 rounded-lg border text-left transition-all flex items-center gap-3 ${
                        selectedMethod === gw.gateway_id
                          ? 'border-primary bg-primary/5 ring-2 ring-primary/20'
                          : 'border-border hover:border-primary/50'
                      }`}
                      data-testid={`method-${gw.gateway_id}`}
                    >
                      {gw.type === 'card'
                        ? <CreditCard className="w-5 h-5 text-indigo-600" />
                        : <Smartphone className="w-5 h-5 text-green-600" />
                      }
                      <div>
                        <p className="text-sm font-medium">{gw.name}</p>
                        {gw.test_mode && <p className="text-[10px] text-amber-600">Mode test</p>}
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            )}

            <form onSubmit={handleCreatePayment} className="space-y-4 mt-2">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>{t(language, 'payments.paymentType')}</Label>
                  <Select value={formData.payment_type} onValueChange={(v) => setFormData({ ...formData, payment_type: v })}>
                    <SelectTrigger data-testid="payment-type-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {paymentTypes.map(type => (
                        <SelectItem key={type} value={type}>{t(language, `payments.${type.toLowerCase()}`)}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>{t(language, 'payments.currency')}</Label>
                  <Select value={formData.currency} onValueChange={(v) => setFormData({ ...formData, currency: v })}>
                    <SelectTrigger data-testid="payment-currency"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="USD">USD</SelectItem>
                      <SelectItem value="EUR">EUR</SelectItem>
                      <SelectItem value="CDF">CDF</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <Label>{t(language, 'payments.amount')}</Label>
                <div className="relative">
                  <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    type="number" step="0.01" min="0.01"
                    value={formData.amount}
                    onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                    className="pl-10" required data-testid="payment-amount"
                  />
                </div>
              </div>

              {needsPhone && (
                <div className="space-y-2">
                  <Label className="flex items-center gap-1.5">
                    <Phone className="w-3.5 h-3.5" />
                    {language === 'fr' ? 'Numéro de téléphone' : 'Phone number'}
                  </Label>
                  <Input
                    type="tel"
                    value={formData.phone_number}
                    onChange={(e) => setFormData({ ...formData, phone_number: e.target.value })}
                    placeholder="+243 XXX XXX XXX"
                    required
                    data-testid="payment-phone"
                  />
                </div>
              )}

              <div className="space-y-2">
                <Label>{t(language, 'payments.payerName')}</Label>
                <Input
                  value={formData.payer_name}
                  onChange={(e) => setFormData({ ...formData, payer_name: e.target.value })}
                  required data-testid="payment-payer"
                />
              </div>

              <div className="space-y-2">
                <Label>Description</Label>
                <Textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  required data-testid="payment-description"
                />
              </div>

              <div className="space-y-2">
                <Label>{language === 'fr' ? 'N° Dossier (optionnel)' : 'Case ID (optional)'}</Label>
                <Input
                  value={formData.case_id}
                  onChange={(e) => setFormData({ ...formData, case_id: e.target.value })}
                  placeholder="case_xxxx" data-testid="payment-case-id"
                />
              </div>

              <div className="flex justify-end gap-3 pt-4">
                <Button type="button" variant="outline" onClick={() => setShowCreateModal(false)}>
                  {t(language, 'common.cancel')}
                </Button>
                <Button type="submit" disabled={creating} className="gap-2" data-testid="create-payment-submit">
                  {isMobileMethod || isMaishaPayMethod ? <Smartphone className="w-4 h-4" /> : <CreditCard className="w-4 h-4" />}
                  {creating ? t(language, 'common.loading') : (language === 'fr' ? 'Payer' : 'Pay')}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>

        {/* Filters */}
        <Card>
          <CardContent className="p-4">
            <div className="flex flex-wrap gap-4 items-end">
              <div className="flex-1 min-w-[200px]">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input placeholder={t(language, 'common.search') + '...'} className="pl-10" data-testid="payments-search" />
                </div>
              </div>
              <div className="w-[150px]">
                <Select value={filters.payment_type || "ALL"} onValueChange={(v) => setFilters({ ...filters, payment_type: v === "ALL" ? "" : v })}>
                  <SelectTrigger data-testid="filter-payment-type"><SelectValue placeholder={t(language, 'payments.paymentType')} /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ALL">{language === 'fr' ? 'Tous' : 'All'}</SelectItem>
                    {paymentTypes.map(type => <SelectItem key={type} value={type}>{t(language, `payments.${type.toLowerCase()}`)}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="w-[150px]">
                <Select value={filters.status || "ALL"} onValueChange={(v) => setFilters({ ...filters, status: v === "ALL" ? "" : v })}>
                  <SelectTrigger data-testid="filter-payment-status"><SelectValue placeholder={t(language, 'payments.status')} /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ALL">{language === 'fr' ? 'Tous' : 'All'}</SelectItem>
                    {statuses.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <Button variant="outline" onClick={loadPayments} className="gap-2">
                <Filter className="w-4 h-4" /> {t(language, 'common.filter')}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Payments Table */}
        <Card>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="data-table">
                <thead>
                  <tr className="bg-muted/50">
                    <th>ID</th>
                    <th>{language === 'fr' ? 'Méthode' : 'Method'}</th>
                    <th>{t(language, 'payments.paymentType')}</th>
                    <th>{t(language, 'payments.amount')}</th>
                    <th>{t(language, 'payments.payerName')}</th>
                    <th>{t(language, 'payments.status')}</th>
                    <th>Date</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    Array.from({ length: 5 }).map((_, i) => (
                      <tr key={i}>{Array.from({ length: 7 }).map((_, j) => <td key={j}><div className="h-4 skeleton" /></td>)}</tr>
                    ))
                  ) : payments.length === 0 ? (
                    <tr>
                      <td colSpan={7} className="text-center py-8 text-muted-foreground">
                        {t(language, 'common.noData')}
                      </td>
                    </tr>
                  ) : (
                    payments.map((payment) => (
                      <tr key={payment.payment_id} data-testid={`payment-row-${payment.payment_id}`}>
                        <td><span className="font-mono text-xs">{payment.payment_id}</span></td>
                        <td>
                          <span className="flex items-center gap-2 text-sm">
                            {getMethodIcon(payment)}
                            {getMethodLabel(payment)}
                          </span>
                        </td>
                        <td>{t(language, `payments.${(payment.payment_type || 'fee').toLowerCase()}`)}</td>
                        <td><span className="font-mono font-medium">{Number(payment.amount || 0).toFixed(2)} {payment.currency}</span></td>
                        <td>{payment.payer_name}</td>
                        <td>{getStatusBadge(payment.status)}</td>
                        <td>
                          <span className="flex items-center gap-1 text-sm text-muted-foreground">
                            <Calendar className="w-3 h-3" />
                            {new Date(payment.created_at).toLocaleDateString(language === 'fr' ? 'fr-FR' : 'en-US')}
                          </span>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
};

export default PaymentsPage;
